import * as React from 'react'
import { ChevronRight, File, Folder, FolderOpen, Trash2 } from 'lucide-react'
import {
  useDeleteIngestedFileMutation,
  useListIngestJobFilesQuery,
  type IngestJobFile
} from '@axilon-apps/domains-ontology-ui-state'
import { IngestFileDeleteDialog } from '../../../components/IngestFileDeleteDialog'
import { useDebugMode } from '../../../lib/debug-mode'
import {
  buildExplorerTree,
  listAllFolderPaths,
  type ExplorerTreeNode,
  type PathItem
} from '../../../components/explorer'
import {
  SourceDocViewer,
  sourceFileBasename,
  useSourceDocViewer,
  type SourceDocRef
} from '../../../components/SourceDocViewer'
import { Card, CardContent } from '../../../components/ui/card'
import { Skeleton } from '../../../components/ui/skeleton'
import { cn } from '../../../lib/utils'

interface FileCoverageProps {
  jobId?: string
  /** Poll while the run is live; stop once terminal. */
  active: boolean
}

const STATUS_ORDER = ['pending', 'ingested', 'skipped', 'unsupported'] as const
type StatusFilter = 'all' | (typeof STATUS_ORDER)[number]

type JobFilePathItem = PathItem & { row: IngestJobFile }

/** Adapt job-file rows into PathItems for the shared explorer tree helpers. */
export function jobFilePathItems(rows: readonly IngestJobFile[]): JobFilePathItem[] {
  return rows.map((row) => ({
    id: row.path,
    name: sourceFileBasename(row.path),
    path: row.path,
    row
  }))
}

/** Human note for a job-file row — reason for exceptions, section for ingested. */
export function jobFileNote(row: IngestJobFile): string | undefined {
  if (row.status === 'ingested') {
    return row.sectionId
      ? `Ingested by ${row.sectionId} parser`
      : 'Ingested from live write evidence'
  }
  const reason = row.reason?.trim()
  return reason || undefined
}

function folderPathsForExpansion(root: ExplorerTreeNode): string[] {
  // Root is "" and not rendered as a collapsible row.
  return listAllFolderPaths(root).filter((path) => path.length > 0)
}

function sortedChildFolders<F extends PathItem, I extends PathItem>(
  node: ExplorerTreeNode<F, I>
): ExplorerTreeNode<F, I>[] {
  return Object.values(node.children).sort((a, b) => a.name.localeCompare(b.name))
}

function countByStatus(rows: IngestJobFile[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const row of rows) {
    counts[row.status] = (counts[row.status] ?? 0) + 1
  }
  return counts
}

/** Shared pill / filter-chip colors per job-file status. */
function statusTone(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
    case 'ingested':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    case 'skipped':
      return 'bg-muted text-muted-foreground'
    case 'unsupported':
      return 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function statusNoteTone(status: string): string {
  switch (status) {
    case 'pending':
      return 'text-amber-700 dark:text-amber-300'
    case 'ingested':
      return 'text-emerald-700 dark:text-emerald-300'
    case 'skipped':
      return 'text-muted-foreground'
    case 'unsupported':
      return 'text-rose-700 dark:text-rose-300'
    default:
      return 'text-muted-foreground'
  }
}

function PathTree({
  root,
  expanded,
  jobId,
  active,
  onToggle,
  onOpenSource,
  onDelete
}: {
  root: ExplorerTreeNode<PathItem, JobFilePathItem>
  expanded: ReadonlySet<string>
  jobId: string
  active: boolean
  onToggle: (path: string) => void
  onOpenSource: (args: SourceDocRef) => void
  /** Present only when file deletion is enabled (debug mode). */
  onDelete?: (item: JobFilePathItem) => void
}) {
  return (
    <ul className="flex flex-col py-1 font-mono text-11 leading-5 text-foreground/90">
      {sortedChildFolders(root).map((node) => (
        <FolderTreeItem
          key={node.path}
          node={node}
          depth={0}
          expanded={expanded}
          jobId={jobId}
          active={active}
          onToggle={onToggle}
          onOpenSource={onOpenSource}
          onDelete={onDelete}
        />
      ))}
      {root.items.map((item) => (
        <FileTreeItem
          key={item.path}
          item={item}
          depth={0}
          jobId={jobId}
          active={active}
          onOpenSource={onOpenSource}
          onDelete={onDelete}
        />
      ))}
    </ul>
  )
}

function FolderTreeItem({
  node,
  depth,
  expanded,
  jobId,
  active,
  onToggle,
  onOpenSource,
  onDelete
}: {
  node: ExplorerTreeNode<PathItem, JobFilePathItem>
  depth: number
  expanded: ReadonlySet<string>
  jobId: string
  active: boolean
  onToggle: (path: string) => void
  onOpenSource: (args: SourceDocRef) => void
  /** Present only when file deletion is enabled (debug mode). */
  onDelete?: (item: JobFilePathItem) => void
}) {
  const open = expanded.has(node.path)
  return (
    <li>
      <button
        type="button"
        className="flex w-full min-w-0 items-center gap-1 px-2 py-0.5 text-left hover:bg-muted/50"
        style={{ paddingLeft: `${0.5 + depth * 0.85}rem` }}
        onClick={() => onToggle(node.path)}
        aria-expanded={open}
      >
        <ChevronRight
          aria-hidden
          className={cn(
            'size-3 shrink-0 text-muted-foreground transition-transform duration-200 ease-out',
            open && 'rotate-90'
          )}
        />
        {open ? (
          <FolderOpen className="size-3 shrink-0 text-muted-foreground" aria-hidden />
        ) : (
          <Folder className="size-3 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <span className="truncate font-medium">{node.name}</span>
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <ul className="min-h-0 overflow-hidden">
          {sortedChildFolders(node).map((child) => (
            <FolderTreeItem
              key={child.path}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              jobId={jobId}
              active={active}
              onToggle={onToggle}
              onOpenSource={onOpenSource}
              onDelete={onDelete}
            />
          ))}
          {node.items.map((item) => (
            <FileTreeItem
              key={item.path}
              item={item}
              depth={depth + 1}
              jobId={jobId}
              active={active}
              onOpenSource={onOpenSource}
              onDelete={onDelete}
            />
          ))}
        </ul>
      </div>
    </li>
  )
}

function FileTreeItem({
  item,
  depth,
  jobId,
  active,
  onOpenSource,
  onDelete
}: {
  item: JobFilePathItem
  depth: number
  jobId: string
  active: boolean
  onOpenSource: (args: SourceDocRef) => void
  /** Present only when file deletion is enabled (debug mode). */
  onDelete?: (item: JobFilePathItem) => void
}) {
  const note = jobFileNote(item.row)
  const status = item.row.status
  return (
    <li className="flex min-w-0 items-center hover:bg-muted/50">
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-1.5 truncate px-2 py-0.5 text-left"
        style={{ paddingLeft: `${0.5 + depth * 0.85}rem` }}
        title={item.path}
        onClick={() =>
          onOpenSource({
            jobId,
            sourceFile: item.path,
            ...(note ? { note, noteStatus: status } : {})
          })
        }
      >
        <File className="size-3 shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate underline-offset-2 hover:underline">{item.name}</span>
        {note ? <span className={cn('truncate', statusNoteTone(status))}> — {note}</span> : null}
      </button>
      {onDelete ? (
        <button
          type="button"
          aria-label={`Delete ${item.name}`}
          title={active ? 'Cannot delete files while ingest is running' : `Delete ${item.name}`}
          disabled={active}
          onClick={() => {
            if (active) return
            onDelete(item)
          }}
          className="mr-1 flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Trash2 className="size-3" />
        </button>
      ) : null}
    </li>
  )
}

function FilterChip({
  label,
  active,
  className,
  onClick
}: {
  label: string
  active: boolean
  className: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-md px-2 py-0.5 text-11 font-medium tracking-wide uppercase transition-[box-shadow,opacity]',
        className,
        active
          ? 'ring-2 ring-foreground/25 ring-offset-1 ring-offset-background'
          : 'opacity-80 hover:opacity-100'
      )}
    >
      {label}
    </button>
  )
}

/**
 * Coverage panel for the agent ingest page — filter chips (all / by status) and
 * a single path tree, sourced from the server ledger.
 */
export function FileCoverage({ jobId, active }: FileCoverageProps) {
  const { data: rows, isLoading } = useListIngestJobFilesQuery(jobId ?? '', {
    skip: !jobId,
    refetchOnMountOrArgChange: true
  })
  const [filter, setFilter] = React.useState<StatusFilter>('all')
  // Lifted above the tree so filter switches / empty-filter unmounts don't reset
  // which folders the user closed. First sighting of a dir still auto-opens.
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set())
  const seenDirsRef = React.useRef<Set<string>>(new Set())
  const { openSource, viewerProps } = useSourceDocViewer()
  // Deleting corpus files is destructive and bypasses the ingest lifecycle, so
  // it is a debug-only affordance until the reaper/inventory work settles.
  const debugMode = useDebugMode()
  const [pendingDelete, setPendingDelete] = React.useState<JobFilePathItem | null>(null)
  const [deleteIngestedFile, deleteState] = useDeleteIngestedFileMutation()
  const resetDelete = deleteState.reset

  const list = rows ?? []
  const counts = React.useMemo(() => countByStatus(list), [list])
  const statuses = STATUS_ORDER.filter((s) => (counts[s] ?? 0) > 0)
  const visible = React.useMemo(
    () => (filter === 'all' ? list : list.filter((r) => r.status === filter)),
    [filter, list]
  )
  const treeKey = React.useMemo(
    () =>
      `${filter}\0${visible
        .map((r) => r.path)
        .sort()
        .join('\0')}`,
    [filter, visible]
  )
  const tree = React.useMemo(
    () => buildExplorerTree<PathItem, JobFilePathItem>([], jobFilePathItems(visible)),
    [treeKey, visible]
  )

  React.useEffect(() => {
    if (visible.length === 0) return
    const dirs = folderPathsForExpansion(tree)
    const fresh: string[] = []
    for (const path of dirs) {
      if (!seenDirsRef.current.has(path)) {
        seenDirsRef.current.add(path)
        fresh.push(path)
      }
    }
    if (fresh.length === 0) return
    setExpanded((prev) => {
      const next = new Set(prev)
      for (const path of fresh) next.add(path)
      return next
    })
  }, [tree, visible.length])

  const toggleFolder = (path: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  if (!jobId) return null
  if (isLoading && !rows) {
    return (
      <Card className="h-[36rem] gap-0 overflow-hidden py-0">
        <CardContent className="p-4">
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (list.length === 0) {
    return (
      <Card className="h-[36rem] gap-0 overflow-hidden py-0">
        <CardContent className="p-4 text-sm text-muted-foreground">
          No job files yet — the inventory seeds when the corpus is uploaded.
        </CardContent>
      </Card>
    )
  }

  return (
    // Fixed height matching Proposed Structure. Kill Card's default py-6/gap-6 so
    // the summary + tree sit at the top of the rail instead of floating in empty padding.
    <Card className="flex h-[36rem] flex-col gap-0 overflow-hidden py-0">
      <CardContent className="flex min-h-0 flex-1 flex-col gap-2 p-3">
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 px-0.5">
          <div className="mr-1 flex items-baseline gap-1.5">
            <span className="font-mono text-xl font-semibold tracking-tight tabular-nums">
              {list.length}
            </span>
            <span className="text-xs text-muted-foreground">files</span>
          </div>
          <FilterChip
            label={`all ${list.length}`}
            active={filter === 'all'}
            className="bg-foreground/5 text-foreground"
            onClick={() => setFilter('all')}
          />
          {statuses.map((status) => (
            <FilterChip
              key={status}
              label={`${status} ${counts[status]}`}
              active={filter === status}
              className={statusTone(status)}
              onClick={() => setFilter(status)}
            />
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border/60">
          {visible.length === 0 ? (
            <p className="m-0 p-3 text-sm text-muted-foreground">No files in this filter.</p>
          ) : (
            <PathTree
              root={tree}
              expanded={expanded}
              jobId={jobId}
              active={active}
              onToggle={toggleFolder}
              onOpenSource={openSource}
              {...(debugMode ? { onDelete: setPendingDelete } : {})}
            />
          )}
        </div>
      </CardContent>
      <SourceDocViewer {...viewerProps} />
      <IngestFileDeleteDialog
        fileName={pendingDelete?.name ?? ''}
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null)
            resetDelete()
          }
        }}
        onConfirm={() => {
          if (!pendingDelete) return
          void deleteIngestedFile({ jobId, path: pendingDelete.path })
            .unwrap()
            .then(() => setPendingDelete(null))
            .catch(() => {
              // Dialog stays open and surfaces deleteState.isError.
            })
        }}
        isDeleting={deleteState.isLoading}
        error={deleteState.isError}
      />
    </Card>
  )
}
